import React from 'react';
import { Grid, Tooltip, Typography } from '@mui/material';

import { GridListItem, type ItemLayoutProps } from 'app/main/components/GridList';
import { ImageAvatar, TagsArray, Text, useSettings, Icon } from 'app/main/components';
import { DateSquareChip } from 'app/main/components/list-items';
import buttons from './DemsButtons';

export default function DemsListItem(props: ItemLayoutProps) {
	const { t } = useSettings();

	const deliveredCount = props.model.dems_sent - props.model.dems_bounced;
	const deliveredPercentage = ((deliveredCount * 100) / props.model.dems_sent).toFixed(2);
	const openedPercentage = ((props.model.dems_opened * 100) / props.model.dems_sent).toFixed(2);
	const clickedPercentage = ((props.model.dems_clicked * 100) / props.model.dems_sent).toFixed(2);
	const unsubscribedPercentage = ((props.model.dems_unsubscribed * 100) / props.model.dems_sent).toFixed(2);

	const avatar = (
		<ImageAvatar
			dimensions={44}
			module="users"
			uid={props.model.dems_user_assigned_uid}
			avatar={props.model.user_assigned_avatar}
			initials={props.model.dems_title_initials}
			labelBadge={props.model.dmst_title_initials}
			labelBadgeTooltip={props.model.dmst_title}
			badgeColor={props.model.dmst_color}
		/>
	);

	return (
		<GridListItem avatar={avatar} buttons={buttons.view?.itemButtons}>
			<Grid container justifyContent="space-between" spacing={1}>
				<Grid size={props.width >= 600 ? 6 : 12}>
					<Grid container>
						<Grid size={12} className="inline-flex items-center">
							{/* WARNING */}
							{props.model.dems_warning_desc && (
								<Tooltip title={props.model.dems_warning_desc} arrow>
									<Icon className="mr-2" icon="alert-circle-outline" color="warning.main" />
								</Tooltip>
							)}
							{/* BLOCKED */}
							{props.model.dems_blocked_desc && (
								<Tooltip title={props.model.dems_blocked_desc} arrow>
									<Icon className="mr-2" icon="block" color="error.main" />
								</Tooltip>
							)}
							{/* TITLE */}
							<Text
								className="font-600"
								variant="subtitle2"
								to={{ module: 'dems', uid: props.model.dems_uid }}
							>
								{props.model.dems_title}
							</Text>
						</Grid>
						{/* DATE */}
						<Grid>
							<DateSquareChip
								value={props.model.dems_date}
								format={{
									year: 'numeric',
									month: '2-digit',
									day: '2-digit',
									hour: '2-digit',
									minute: 'numeric'
								}}
							/>
						</Grid>
						{/* TAGS */}
						<Grid>
							<TagsArray tags={props.model.dems_tags} changeFilter={props.changeFilter} />
						</Grid>
					</Grid>
				</Grid>
				{/* STATISTICS */}
				{props.width >= 600 && (
					<Grid size={6}>
						{props.model.dems_sent >= 0 && (
							<Grid container justifyContent="space-evenly">
								{/* SENT */}
								<Grid size={2}>
									<Typography color="textSecondary">{t('DEMS_STATISTICS_SENT')}</Typography>
									<Typography variant="h5">{props.model.dems_sent}</Typography>
								</Grid>
								{/* DELIVERED */}
								<Grid size={2}>
									<Typography align="right" color="textSecondary">
										{t('DEMS_STATISTICS_DELIVERED')}
									</Typography>
									<Typography align="right" variant="h5">
										{deliveredCount}
									</Typography>
									<Typography color="textSecondary" align="right">
										{`${deliveredPercentage}% `}
									</Typography>
								</Grid>
								{/* OPENED */}
								<Grid size={2}>
									<Typography align="right" color="textSecondary">
										{t('DEMS_STATISTICS_OPENED')}
									</Typography>
									<Typography align="right" variant="h5">
										{props.model.dems_opened}
									</Typography>
									<Typography color="textSecondary" align="right">
										{`${openedPercentage}% `}
									</Typography>
								</Grid>
								{/* CLICKED */}
								<Grid size={2}>
									<Typography align="right" color="textSecondary">
										{t('DEMS_STATISTICS_CLICKED')}
									</Typography>
									<Typography align="right" variant="h5">
										{props.model.dems_clicked}
									</Typography>
									<Typography color="textSecondary" align="right">
										{`${clickedPercentage}% `}
									</Typography>
								</Grid>
								{/* UNSUBSCRIBE */}
								<Grid size={2}>
									<Typography align="right" color="textSecondary">
										{t('DEMS_STATISTICS_UNSUBSCRIBED')}
									</Typography>
									<Typography align="right" variant="h5">
										{props.model.dems_unsubscribed}
									</Typography>
									<Typography color="textSecondary" align="right">
										{`${unsubscribedPercentage}% `}
									</Typography>
								</Grid>
							</Grid>
						)}
					</Grid>
				)}
			</Grid>
		</GridListItem>
	);
}
